import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Collapse, List, message, Modal, Progress, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { CloseOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import QuantityControl from "../../../../../components/shared-components/QuantityControl";
import dayjs from "dayjs";
import styled from "styled-components";

const { Text, Title } = Typography;

const CRATE_STOCK_BARCODE_MAX_LENGTH = 99;

const normalizeBarcode = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str) return "";
  const digits = str.replace(/\D/g, "");
  return digits.replace(/^0+/, "");
};

const sanitizeCrateDocId = (crateId) => {
  if (!crateId) return crateId;
  return String(crateId).replaceAll("/", "_").trim();
};

const ScanBundleItemsModal = ({
  visible,
  onCancel,
  onSave,
  bundleProduct,
  requestedBundles,
  products,
  crateStocks,
  findCrateStockByCrateId,
  getCrateStockItem,
  getCrateShelfLifeStatus,
  handlePickingCrateScan,
  applySoldToCrate,
  appendCratePickEvent,
  LOG_ACTION_SOLD,
  order,
}) => {
  const { t } = useTranslation();

  const [scannedItems, setScannedItems] = useState({});
  const [activeProductId, setActiveProductId] = useState(null);
  // Per-product crate state: { [productId]: { pickingCrate, pickingScannedCount, pickingBaseFulfilled, awaitingCrateScan, emptiedCrates, pickedCrates } }
  const [productCrateState, setProductCrateState] = useState({});
  const [manualCrateModalVisible, setManualCrateModalVisible] = useState(false);
  const [manualCrateId, setManualCrateId] = useState("");
  const [manualCrateSubmitting, setManualCrateSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [productDetailVisible, setProductDetailVisible] = useState(false);
  const [collapseActiveKeys, setCollapseActiveKeys] = useState([]);

  const bundleItems = useMemo(() => bundleProduct?.bundleItems || [], [bundleProduct?.bundleItems]);
  const packagingMaterial = bundleProduct?.packagingMaterial || null;
  const hasPackaging = packagingMaterial && parseFloat(packagingMaterial.price || 0) > 0;

  useEffect(() => {
    if (visible) {
      setScannedItems({});
      setActiveProductId(bundleItems[0]?.productId || null);
      setProductCrateState({});
      setCollapseActiveKeys([]);
    }
  }, [visible, bundleItems]);

  useEffect(() => {
    if (!visible) return;

    let barcode = "";

    const handleScannerInput = (e) => {
      if (manualCrateModalVisible) return;

      if (e.code?.includes?.("Digit") || /^[0-9]$/.test(e.key)) {
        barcode += e.key;
      }
      if (!!barcode.length && e.key === "Enter") {
        if (barcode.length > CRATE_STOCK_BARCODE_MAX_LENGTH) {
          message.error(t("notifications.error_invalid_barcode"));
        }
        handleBarcodeScan(barcode).then(() => (barcode = ""));
      }
    };

    document.addEventListener("keydown", handleScannerInput);
    return () => {
      document.removeEventListener("keydown", handleScannerInput);
    };
  }, [visible, activeProductId, productCrateState, manualCrateModalVisible, scannedItems, productDetailVisible]);

  const getActiveProduct = () => {
    if (!activeProductId) return null;
    return products?.find((p) => p.productId === activeProductId) || null;
  };

  // Get current product's crate state
  const getCurrentCrateState = () => {
    if (!activeProductId) return {};
    return (
      productCrateState[activeProductId] || {
        pickingCrate: null,
        pickingScannedCount: 0,
        pickingBaseFulfilled: 0,
        awaitingCrateScan: false,
        emptiedCrates: [],
        pickedCrates: [],
      }
    );
  };

  // Update current product's crate state
  const updateCurrentCrateState = (updates) => {
    if (!activeProductId) return;
    setProductCrateState((prev) => ({
      ...prev,
      [activeProductId]: {
        ...getCurrentCrateState(),
        ...updates,
      },
    }));
  };

  const pickingCrate = getCurrentCrateState().pickingCrate;
  const pickingScannedCount = getCurrentCrateState().pickingScannedCount;
  const pickingBaseFulfilled = getCurrentCrateState().pickingBaseFulfilled;
  const awaitingCrateScan = getCurrentCrateState().awaitingCrateScan;
  const emptiedCrates = getCurrentCrateState().emptiedCrates;
  const pickedCrates = getCurrentCrateState().pickedCrates;

  const getActiveProductBarcode = () => {
    const product = getActiveProduct();
    return normalizeBarcode(product?.barcode);
  };

  const handleCrateScan = async (crateBarcode, overrideProductId = null) => {
    const targetProductId = overrideProductId ?? activeProductId;

    const getTargetCrateState = () => {
      if (!targetProductId) return {};
      return (
        productCrateState[targetProductId] || {
          pickingCrate: null,
          pickingScannedCount: 0,
          pickingBaseFulfilled: 0,
          awaitingCrateScan: false,
          emptiedCrates: [],
          pickedCrates: [],
        }
      );
    };

    const updateTargetCrateState = (updates) => {
      if (!targetProductId) return;
      setProductCrateState((prev) => ({
        ...prev,
        [targetProductId]: {
          ...(prev[targetProductId] || {}),
          ...updates,
        },
      }));
    };

    const code = String(crateBarcode || "").trim();
    if (!code) return;
    if (!/^[0-9]+$/.test(code) || code.length > CRATE_STOCK_BARCODE_MAX_LENGTH) {
      message.error(t("notifications.error_invalid_barcode"));
      return false;
    }

    const currentEmptiedCrates = getTargetCrateState().emptiedCrates || [];
    if (
      currentEmptiedCrates.some(
        (c) => String(c?.crateId || "").trim() === code || String(c?.crateDocId || "").trim() === code,
      )
    ) {
      message.error(t("order_picking.crate_already_marked_empty"));
      return false;
    }

    const crate = findCrateStockByCrateId(code);
    if (!crate) {
      message.error(t("order_picking.unknown_crate"));
      return false;
    }

    const crateDocId = crate.id || sanitizeCrateDocId(crate.crateId);
    if (currentEmptiedCrates.some((c) => String(c?.crateDocId || "").trim() === String(crateDocId || "").trim())) {
      message.error(t("order_picking.crate_already_marked_empty"));
      return false;
    }

    const targetProduct = products?.find((p) => p.productId === targetProductId);
    const targetProductBarcode = normalizeBarcode(targetProduct?.barcode);

    const item = getCrateStockItem(crate, targetProductBarcode || targetProductId);
    const itemQty = Number(item?.quantity || 0);
    if (!item || itemQty <= 0) {
      message.error(t("order_picking.crate_is_empty"));
      return false;
    }
    const crateItemBarcode = normalizeBarcode(item?.barcode);
    const crateMatchesTarget = Boolean(
      targetProductBarcode && crateItemBarcode && crateItemBarcode === targetProductBarcode,
    );

    if (!crateMatchesTarget) {
      message.error(t("order_picking.crate_does_not_contain_product"));
      return false;
    }

    const shelfStatus = getCrateShelfLifeStatus(crate);
    if (shelfStatus.overShelf) {
      return new Promise((resolve) => {
        Modal.confirm({
          title: t("order_picking.crate_product_expired_confirm_title"),
          content: t("order_picking.crate_product_expired_confirm_message"),
          okText: t("order_picking.yes_continue"),
          cancelText: t("order_picking.no_cancel"),
          onOk: () => {
            const currentAwaitingCrateScan = getTargetCrateState().awaitingCrateScan;
            updateTargetCrateState({
              pickingCrate: crate,
              pickingScannedCount: 0,
              awaitingCrateScan: false,
              pickingBaseFulfilled: currentAwaitingCrateScan
                ? Number(scannedItems[targetProductId] || 0)
                : getTargetCrateState().pickingBaseFulfilled,
            });
            resolve(true);
          },
          onCancel: () => {
            resolve(false);
          },
        });
      });
    }
    if (shelfStatus.overInternal) {
      message.warning(t("order_picking.crate_product_over_internal_shelf_life"));
    }

    const currentAwaitingCrateScan = getTargetCrateState().awaitingCrateScan;
    updateTargetCrateState({
      pickingCrate: crate,
      pickingScannedCount: 0,
      awaitingCrateScan: false,
      pickingBaseFulfilled: currentAwaitingCrateScan
        ? Number(scannedItems[targetProductId] || 0)
        : getTargetCrateState().pickingBaseFulfilled,
    });

    return true;
  };

  const handleProductScan = async (productBarcode) => {
    const expected = getActiveProductBarcode();
    const scanned = normalizeBarcode(productBarcode);
    if (!expected) return;
    if (scanned !== expected) {
      message.error(t("notifications.error_unknown_barcode"));
      return;
    }

    // Get the active product's quantity per bundle to calculate max allowed
    const activeItem =
      bundleItems.find((item) => item.productId === activeProductId) ||
      (hasPackaging && packagingMaterial?.productId === activeProductId ? packagingMaterial : null);
    const quantityPerBundle = activeItem?.quantity || 1;
    const maxAllowedForProduct = getTotalNeeded(activeProductId, quantityPerBundle);

    const currentPickingCrate = getCurrentCrateState().pickingCrate;
    if (!currentPickingCrate) {
      const currentScanned = Number(scannedItems[activeProductId] || 0);
      if (currentScanned >= maxAllowedForProduct) {
        message.error(t("bundle_scanning.max_quantity_reached"));
        return;
      }
      setScannedItems((prev) => ({
        ...prev,
        [activeProductId]: Number(prev[activeProductId] || 0) + 1,
      }));
      return;
    }

    const activeProduct = getActiveProduct();
    const activeProductBarcode = normalizeBarcode(activeProduct?.barcode);
    const crateQty = Number(
      getCrateStockItem(currentPickingCrate, activeProductBarcode || activeProductId)?.quantity || 0,
    );
    if (crateQty <= 0) {
      message.error(t("order_picking.crate_is_empty"));
      return;
    }

    const currentPickingBaseFulfilled = getCurrentCrateState().pickingBaseFulfilled;
    const currentPickingScannedCount = getCurrentCrateState().pickingScannedCount;
    const base = Number(currentPickingBaseFulfilled || 0);
    const nextScanned = Number(currentPickingScannedCount || 0) + 1;

    if (nextScanned > crateQty) {
      message.error(t("order_picking.quantity_exceeds_items_in_crate"));
      return;
    }

    const currentScanned = Number(scannedItems[activeProductId] || 0);
    if (currentScanned >= maxAllowedForProduct) {
      message.error(t("bundle_scanning.max_quantity_reached"));
      return;
    }

    const maxAllowed = base + nextScanned;
    updateCurrentCrateState({ pickingScannedCount: nextScanned });
    setScannedItems((prev) => ({
      ...prev,
      [activeProductId]: Math.min(maxAllowed, Number(prev[activeProductId] || 0) + 1),
    }));
  };

  const handleBarcodeScan = async (barcode) => {
    const code = String(barcode || "").trim();
    const currentAwaitingCrateScan = getCurrentCrateState().awaitingCrateScan;
    const currentPickingCrate = getCurrentCrateState().pickingCrate;

    if (currentAwaitingCrateScan || !currentPickingCrate) {
      if (/^[0-9]+$/.test(code) && code.length <= CRATE_STOCK_BARCODE_MAX_LENGTH) {
        if (!productDetailVisible) {
          const crate = findCrateStockByCrateId(code);
          if (crate) {
            const crateItem = getCrateStockItem(crate);
            const crateItemBarcode = normalizeBarcode(crateItem?.barcode);
            if (crateItemBarcode) {
              const allItems = [
                ...bundleItems.map((item) => ({ ...item, isPackaging: false })),
                ...(hasPackaging ? [{ ...packagingMaterial, isPackaging: true }] : []),
              ];
              const matchedItem = allItems.find((bundleItem) => {
                const product = products?.find((p) => p.productId === bundleItem.productId);
                return normalizeBarcode(product?.barcode) === crateItemBarcode;
              });
              if (matchedItem) {
                setActiveProductId(matchedItem.productId);
                setProductDetailVisible(true);
                await handleCrateScan(code, matchedItem.productId);
                return;
              }
            }
          }
        }
        await handleCrateScan(code);
        return;
      }

      await handleProductScan(code);
      return;
    }

    await handleProductScan(code);
  };

  const getBundleRatios = () => {
    const ratios = [];

    bundleItems.forEach((item) => {
      const scanned = scannedItems[item.productId] || 0;
      const perBundle = item.quantity;
      ratios.push(Math.floor(scanned / perBundle));
    });

    if (hasPackaging) {
      const scanned = scannedItems[packagingMaterial.productId] || 0;
      const perBundle = packagingMaterial.quantity;
      ratios.push(Math.floor(scanned / perBundle));
    }

    return ratios;
  };

  const calculateCompletedBundles = () => {
    const ratios = getBundleRatios();
    return ratios.length > 0 ? Math.min(...ratios) : 0;
  };

  const isConsistentBundleCount = () => {
    const ratios = [];

    for (const item of bundleItems) {
      const scanned = scannedItems[item.productId] || 0;
      if (scanned % item.quantity !== 0) return false;
      ratios.push(scanned / item.quantity);
    }

    if (hasPackaging) {
      const scanned = scannedItems[packagingMaterial.productId] || 0;
      if (scanned % packagingMaterial.quantity !== 0) return false;
      ratios.push(scanned / packagingMaterial.quantity);
    }

    if (ratios.length === 0) return false;
    const first = ratios[0];
    return ratios.every((r) => r === first);
  };

  const getTotalNeeded = (productId, quantityPerBundle) => {
    return requestedBundles * quantityPerBundle;
  };

  const getProductProgress = (productId, quantityPerBundle) => {
    const scanned = scannedItems[productId] || 0;
    const needed = getTotalNeeded(productId, quantityPerBundle);
    return needed > 0 ? (scanned / needed) * 100 : 0;
  };

  const isProductComplete = (productId, quantityPerBundle) => {
    const scanned = scannedItems[productId] || 0;
    const needed = getTotalNeeded(productId, quantityPerBundle);
    return scanned >= needed;
  };

  const handleSave = async () => {
    const completedBundles = calculateCompletedBundles();

    if (completedBundles === 0) {
      message.warning(t("bundle_scanning.no_complete_bundles"));
      return;
    }

    if (!isConsistentBundleCount()) {
      message.warning(t("bundle_scanning.inconsistent_bundle_counts"));
      return;
    }

    setIsSaving(true);
    try {
      // Collect all picked and emptied crates from all products
      const allPickedCrates = [];
      const allEmptiedCrates = [];

      // Get all product IDs that have scanned items (including packaging)
      const allProductIds = [
        ...bundleItems.map((item) => item.productId),
        ...(hasPackaging ? [packagingMaterial.productId] : []),
      ];

      // Collect crates from all products
      allProductIds.forEach((productId) => {
        const state = productCrateState[productId] || {};

        // Add already emptied/picked crates
        if (state.pickedCrates) {
          allPickedCrates.push(...state.pickedCrates);
        }
        if (state.emptiedCrates) {
          allEmptiedCrates.push(...state.emptiedCrates);
        }

        // Add current active crate if it has scanned items
        if (state.pickingCrate && Number(state.pickingScannedCount || 0) > 0) {
          const crateDocId = state.pickingCrate.id || sanitizeCrateDocId(state.pickingCrate.crateId);
          const crateId = state.pickingCrate.crateId || state.pickingCrate.id;
          const product = products?.find((p) => p.productId === productId);
          const productBarcode = normalizeBarcode(product?.barcode);
          const item = getCrateStockItem(state.pickingCrate, productBarcode || productId);
          const pickedQty = Number(state.pickingScannedCount || 0);

          // Only add if not already in pickedCrates
          const alreadyAdded = allPickedCrates.some(
            (c) => String(c?.crateDocId || "").trim() === String(crateDocId || "").trim(),
          );

          if (!alreadyAdded) {
            allPickedCrates.push({
              crateId: String(crateId || "").trim(),
              crateDocId: String(crateDocId || "").trim(),
              pickedQty,
              productId: productId,
              productBarcode: item?.barcode || null,
              status: "active",
              createdAtUnix: dayjs().unix(),
            });
          }
        }
      });

      await onSave({
        completedBundles,
        scannedItems,
        pickedCrates: allPickedCrates,
        emptiedCrates: allEmptiedCrates,
        currentPickingCrate: null,
        currentPickingScannedCount: 0,
        currentActiveProductId: null,
      });

      setScannedItems({});
      setProductCrateState({});
    } catch (error) {
      console.error("Error saving bundle items:", error);
      message.error(t("error_saving_progress"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmptyCrate = () => {
    const currentPickingCrate = getCurrentCrateState().pickingCrate;
    const currentPickingScannedCount = getCurrentCrateState().pickingScannedCount;
    const currentPickedCrates = getCurrentCrateState().pickedCrates || [];
    const currentEmptiedCrates = getCurrentCrateState().emptiedCrates || [];

    if (!currentPickingCrate) return;

    const crateDocId = currentPickingCrate.id || sanitizeCrateDocId(currentPickingCrate.crateId);
    const crateId = currentPickingCrate.crateId || currentPickingCrate.id;
    const activeProduct = getActiveProduct();
    const activeProductBarcode = normalizeBarcode(activeProduct?.barcode);
    const item = getCrateStockItem(currentPickingCrate, activeProductBarcode || activeProductId);
    const pickedQty = Number(currentPickingScannedCount || 0);

    const newPickedCrates = [...currentPickedCrates];
    const newEmptiedCrates = [...currentEmptiedCrates];

    if (pickedQty > 0) {
      const exists = newPickedCrates.some(
        (c) => String(c?.crateDocId || "").trim() === String(crateDocId || "").trim(),
      );
      if (!exists) {
        newPickedCrates.push({
          crateId: String(crateId || "").trim(),
          crateDocId: String(crateDocId || "").trim(),
          pickedQty,
          productId: activeProductId,
          productBarcode: item?.barcode || null,
          status: "emptied",
          createdAtUnix: dayjs().unix(),
        });
      }
    }

    const exists = newEmptiedCrates.some((c) => String(c?.crateDocId || "").trim() === String(crateDocId || "").trim());
    if (!exists) {
      newEmptiedCrates.push({
        crateId: String(crateId || "").trim(),
        crateDocId: String(crateDocId || "").trim(),
        scannedCount: pickedQty,
        createdAtUnix: dayjs().unix(),
      });
    }

    updateCurrentCrateState({
      pickedCrates: newPickedCrates,
      emptiedCrates: newEmptiedCrates,
      pickingBaseFulfilled: Number(scannedItems[activeProductId] || 0),
      awaitingCrateScan: true,
      pickingCrate: null,
      pickingScannedCount: 0,
    });
  };

  const completedBundles = calculateCompletedBundles();
  const bundleProgress = requestedBundles > 0 ? Math.round((completedBundles / requestedBundles) * 100 * 100) / 100 : 0;

  const allProductItems = [
    ...bundleItems.map((item) => ({ ...item, isPackaging: false })),
    ...(hasPackaging ? [{ ...packagingMaterial, isPackaging: true }] : []),
  ];

  const activeItem =
    bundleItems.find((i) => i.productId === activeProductId) ||
    (hasPackaging && packagingMaterial?.productId === activeProductId ? packagingMaterial : null);
  const isPackagingActive = hasPackaging && packagingMaterial?.productId === activeProductId;
  const activeQuantityPerBundle = activeItem?.quantity || 1;
  const activeTotalNeeded = activeProductId ? getTotalNeeded(activeProductId, activeQuantityPerBundle) : 0;
  const activeScanned = activeProductId ? scannedItems[activeProductId] || 0 : 0;
  const activeProduct = getActiveProduct();

  const handleProductCardClick = (productId) => {
    setActiveProductId(productId);
    setProductDetailVisible(true);
  };

  const handleSubModalUnpick = () => {
    if (!activeProductId) return;
    setScannedItems((prev) => ({ ...prev, [activeProductId]: 0 }));
    updateCurrentCrateState({
      pickingCrate: null,
      pickingScannedCount: 0,
      pickingBaseFulfilled: 0,
      awaitingCrateScan: false,
      emptiedCrates: [],
      pickedCrates: [],
    });
  };

  return (
    <>
      {/* Main bundle modal — product list */}
      <Modal
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {bundleProduct?.productName}
            </Title>
            <Text type="secondary">{t("bundle_scanning.scan_mode")}</Text>
          </div>
        }
        open={visible}
        onCancel={onCancel}
        width="95%"
        style={{ top: 20, maxWidth: 600 }}
        footer={[
          <div key="footer" style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
            <Button key="cancel" size="large" onClick={onCancel} icon={<CloseOutlined />}>
              {t("cancel")}
            </Button>
            <Button
              key="save"
              type="primary"
              size="large"
              onClick={handleSave}
              loading={isSaving}
              disabled={completedBundles === 0 || !isConsistentBundleCount()}
            >
              {t("save")} ({completedBundles} {t("bundle_scanning.bundles")})
            </Button>
          </div>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }} size={16}>
          <Card size="small" style={{ backgroundColor: "#e6f4ff" }}>
            <Space direction="vertical" style={{ width: "100%" }} size={8}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text strong>{t("bundle_scanning.requested_bundles")}:</Text>
                <Text strong style={{ fontSize: 18 }}>
                  {requestedBundles}
                </Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text strong>{t("bundle_scanning.completed_bundles")}:</Text>
                <Text
                  strong
                  style={{ fontSize: 18, color: completedBundles >= requestedBundles ? "#52c41a" : "#1890ff" }}
                >
                  {completedBundles}
                </Text>
              </div>
              <Progress
                percent={Math.min(bundleProgress, 100)}
                status={completedBundles >= requestedBundles ? "success" : "active"}
                strokeColor={completedBundles >= requestedBundles ? "#52c41a" : "#1890ff"}
              />
            </Space>
          </Card>

          <Collapse
            activeKey={collapseActiveKeys}
            onChange={setCollapseActiveKeys}
            items={[
              {
                key: "composition",
                label: (
                  <Space>
                    <InfoCircleOutlined />
                    <Text strong>{t("bundle_scanning.bundle_composition")}</Text>
                  </Space>
                ),
                children: (
                  <Space direction="vertical" style={{ width: "100%" }} size={4}>
                    <Text type="secondary">{t("bundle_scanning.each_bundle_contains")}:</Text>
                    {bundleItems.map((item) => {
                      const product = products?.find((p) => p.productId === item.productId);
                      return (
                        <div key={item.productId}>
                          <Text>
                            • {item.quantity}x {product?.productName || item.productId}
                          </Text>
                        </div>
                      );
                    })}
                    {hasPackaging && (
                      <div>
                        <Text>
                          • {packagingMaterial.quantity}x{" "}
                          {products?.find((p) => p.productId === packagingMaterial.productId)?.productName ||
                            packagingMaterial.productId}{" "}
                          <Badge
                            count={t("bundle_scanning.packaging")}
                            style={{ backgroundColor: "#52c41a", marginLeft: 4 }}
                          />
                        </Text>
                      </div>
                    )}
                  </Space>
                ),
              },
            ]}
          />

          <div>
            <Text strong style={{ marginBottom: 8, display: "block" }}>
              {t("products_to_fulfill")}
            </Text>
            <List
              dataSource={allProductItems}
              renderItem={(item) => {
                const product = products?.find((p) => p.productId === item.productId);
                const productName = product?.productName || item.productId;
                const totalNeeded = getTotalNeeded(item.productId, item.quantity);
                const scanned = scannedItems[item.productId] || 0;
                const isComplete = isProductComplete(item.productId, item.quantity);
                return (
                  <List.Item
                    onClick={() => handleProductCardClick(item.productId)}
                    style={{
                      cursor: "pointer",
                      backgroundColor: isComplete ? "#dcfce7" : "white",
                      border: "1px solid #f0f0f0",
                      borderRadius: "8px",
                      marginBottom: "8px",
                      padding: "12px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                      <div style={{ marginRight: "12px" }}>
                        <Text strong>
                          {scanned}/{totalNeeded}
                        </Text>
                      </div>
                      <div style={{ flex: 1 }}>
                        <Text>{productName}</Text>
                        {item.isPackaging && (
                          <Badge
                            count={t("bundle_scanning.packaging")}
                            style={{ backgroundColor: "#52c41a", marginLeft: 8 }}
                          />
                        )}
                      </div>
                      <div>
                        <Badge
                          status={isComplete ? "success" : "warning"}
                          text={isComplete ? t("ready") : t("pending")}
                        />
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          </div>
        </Space>
      </Modal>

      {/* Product detail sub-modal — identical style to ProductDetailModal */}
      <Modal
        title={<h1>{activeProduct?.productName}</h1>}
        open={productDetailVisible}
        onCancel={() => setProductDetailVisible(false)}
        footer={[
          <div key="footer">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <Button key="unpick" onClick={handleSubModalUnpick} size="large">
                  {t("unpick")}
                </Button>
              </div>
              <div>
                <Button key="done" type="primary" size="large" onClick={() => setProductDetailVisible(false)}>
                  {t("done")}
                </Button>
              </div>
            </div>
          </div>,
        ]}
      >
        {activeProductId && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ width: "100%", marginBottom: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(getCurrentCrateState().emptiedCrates || []).map((c) => (
                  <div
                    key={`emptied-${c.crateDocId || c.crateId}`}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t("order_picking.crate_empty_scanned_line", {
                        crateId: c.crateId || c.crateDocId,
                        scannedCount: Number(c.scannedCount || 0),
                      })}
                    </Text>
                  </div>
                ))}

                {!isPackagingActive && (
                  <>
                    {!getCurrentCrateState().pickingCrate ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Text style={{ fontSize: 12 }}>{t("order_picking.scan_crate")}</Text>
                        <Space size={4}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t("order_picking.waiting_for_crate_scan")}
                          </Text>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => {
                              setManualCrateId("");
                              setManualCrateModalVisible(true);
                            }}
                          />
                        </Space>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Text style={{ fontSize: 12 }}>
                          {t("order_picking.current_crate", {
                            crateId:
                              getCurrentCrateState().pickingCrate?.crateId || getCurrentCrateState().pickingCrate?.id,
                          })}
                        </Text>
                        <Space size={6}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t("order_picking.scanned_count", {
                              count: getCurrentCrateState().pickingScannedCount,
                            })}
                          </Text>
                          <Button size="small" onClick={handleEmptyCrate}>
                            {t("order_picking.empty")}
                          </Button>
                        </Space>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <QuantityDisplayContainer>
              <QuantityColumn>
                <QuantityLabel>{t("requested")}</QuantityLabel>
                <QuantityValue fontSize="56px" color="#4A5568">
                  {activeTotalNeeded}
                </QuantityValue>
              </QuantityColumn>
              <QuantityColumn>
                <QuantityLabel>{t("fulfilled")}</QuantityLabel>
                <QuantityValue fontSize="64px" color="#9B2C6B">
                  {activeScanned}
                </QuantityValue>
              </QuantityColumn>
            </QuantityDisplayContainer>

            <div style={{ marginBottom: "40px" }}>
              <QuantityControl
                isMobile={true}
                isPickingFlow={true}
                productId={activeProductId}
                currentQuantity={activeScanned}
                onIncrease={() => {
                  if (!activeItem) return;
                  if (activeScanned >= activeTotalNeeded) {
                    message.error(t("bundle_scanning.max_quantity_reached"));
                    return;
                  }
                  const currentPickingCrate = getCurrentCrateState().pickingCrate;
                  const currentAwaitingCrateScan = getCurrentCrateState().awaitingCrateScan;
                  if (!currentPickingCrate || currentAwaitingCrateScan || isPackagingActive) {
                    setScannedItems((prev) => ({
                      ...prev,
                      [activeProductId]: Number(prev[activeProductId] || 0) + 1,
                    }));
                    return;
                  }
                  const base = Number(getCurrentCrateState().pickingBaseFulfilled || 0);
                  const scannedCount = Number(getCurrentCrateState().pickingScannedCount || 0);
                  const next = activeScanned + 1;
                  const crateQty = Number(
                    getCrateStockItem(currentPickingCrate, getActiveProductBarcode() || activeProductId)?.quantity || 0,
                  );
                  const absoluteMax = base + crateQty;
                  if (next > absoluteMax) {
                    message.error(t("order_picking.quantity_exceeds_items_in_crate"));
                    return;
                  }
                  const maxAllowed = base + scannedCount;
                  if (next > maxAllowed) {
                    updateCurrentCrateState({
                      pickingScannedCount: scannedCount + (next - maxAllowed),
                    });
                  }
                  setScannedItems((prev) => ({ ...prev, [activeProductId]: next }));
                }}
                onDecrease={() => {
                  setScannedItems((prev) => ({
                    ...prev,
                    [activeProductId]: Math.max(0, (prev[activeProductId] || 0) - 1),
                  }));
                }}
                onManualInput={(productId, value) => {
                  if (!activeItem) return;
                  const next = Math.max(0, Number(value || 0));
                  if (next > activeTotalNeeded) {
                    message.error(t("bundle_scanning.max_quantity_reached"));
                    return;
                  }
                  const currentPickingCrate = getCurrentCrateState().pickingCrate;
                  const currentAwaitingCrateScan = getCurrentCrateState().awaitingCrateScan;
                  if (!currentPickingCrate || currentAwaitingCrateScan || isPackagingActive) {
                    setScannedItems((prev) => ({ ...prev, [activeProductId]: next }));
                    return;
                  }
                  const base = Number(getCurrentCrateState().pickingBaseFulfilled || 0);
                  const crateQty = Number(
                    getCrateStockItem(currentPickingCrate, getActiveProductBarcode() || activeProductId)?.quantity || 0,
                  );
                  const absoluteMax = base + crateQty;
                  if (next > absoluteMax) {
                    message.error(t("order_picking.quantity_exceeds_items_in_crate"));
                    return;
                  }
                  const scannedCount = Number(getCurrentCrateState().pickingScannedCount || 0);
                  const maxAllowed = base + scannedCount;
                  if (next > maxAllowed) {
                    updateCurrentCrateState({
                      pickingScannedCount: scannedCount + (next - maxAllowed),
                    });
                  }
                  setScannedItems((prev) => ({ ...prev, [activeProductId]: next }));
                }}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Manual crate entry modal */}
      <Modal
        title={t("order_picking.manual_crate_entry_title")}
        open={manualCrateModalVisible}
        onCancel={() => {
          setManualCrateModalVisible(false);
          setManualCrateId("");
        }}
        okText={t("ok")}
        cancelText={t("cancel")}
        confirmLoading={manualCrateSubmitting}
        onOk={async () => {
          const code = String(manualCrateId || "").trim();
          if (!code) return;

          setManualCrateSubmitting(true);
          try {
            const success = await handleCrateScan(code);
            if (success) {
              setManualCrateModalVisible(false);
              setManualCrateId("");
            }
          } finally {
            setManualCrateSubmitting(false);
          }
        }}
      >
        <input
          type="text"
          inputMode="numeric"
          value={manualCrateId}
          onChange={(e) => setManualCrateId(e.target.value)}
          placeholder={t("order_picking.manual_crate_entry_placeholder")}
          autoFocus
          style={{
            width: "100%",
            padding: "8px 12px",
            fontSize: "16px",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const code = String(manualCrateId || "").trim();
              if (!code) return;

              setManualCrateSubmitting(true);
              handleCrateScan(code)
                .then((success) => {
                  if (success) {
                    setManualCrateModalVisible(false);
                    setManualCrateId("");
                  }
                })
                .finally(() => {
                  setManualCrateSubmitting(false);
                });
            }
          }}
        />
      </Modal>
    </>
  );
};

const QuantityDisplayContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 20px;
  margin-bottom: 20px;
  gap: 30px;
`;

const QuantityColumn = styled.div`
  text-align: center;
`;

const QuantityLabel = styled(Text)`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #666;
  display: block;
`;

const QuantityValue = styled(Text)`
  font-size: ${(props) => props.fontSize || "56px"};
  font-weight: bold;
  color: ${(props) => props.color || "#4A5568"};
  line-height: 64px;
`;

export default ScanBundleItemsModal;
